import { ComponentProps, FC, ReactNode } from "react";
import { Row, Col, Tag, Typography, Grid, Badge } from "antd";
import { RightOutlined, CalendarOutlined } from "@ant-design/icons";

import { Nullable, classname } from "@ctra/utils";

import { Barn, Icon } from "../../../elements";
import MobileViewRow from "../MobileViewRow";
import styles from "./InsightItem.module.less";

const { Text } = Typography;

export interface InsightItemProps extends ComponentProps<typeof Row> {
  /**
   * insight icon
   */
  icon: ReactNode;
  /**
   * title
   */
  title: string;
  /**
   * farm name
   */
  farm?: string;
  /**
   * insight time period
   */
  period: ReactNode;
  /**
   * group
   */
  group?: Nullable<string>;
  /**
   * unseen indicator
   */
  unseen?: boolean;
}

/**
 * Mobile view
 * @param {string | undefined} className
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} icon
 * @param {string} title
 * @param {string | undefined} farm
 * @param {boolean | undefined} unseen
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} period
 * @param {<string> | undefined} group
 * @param {Pick<InsightItemProps & {children?: React.ReactNode | undefined}, "gutter" | "align" | "justify" | "prefixCls" | "wrap" | "defaultChecked" | "defaultValue" | "suppressContentEditableWarning" | "suppressHydrationWarning" | "accessKey" | "contentEditable" | "contextMenu" | "dir" | "draggable" | "hidden" | "id" | "lang" | "placeholder" | "slot" | "spellCheck" | "style" | "tabIndex" | "translate" | "radioGroup" | "role" | "about" | "datatype" | "inlist" | "prefix" | "property" | "resource" | "typeof" | "vocab" | "autoCapitalize" | "autoCorrect" | "autoSave" | "color" | "itemProp" | "itemScope" | "itemType" | "itemID" | "itemRef" | "results" | "security" | "unselectable" | "inputMode" | "is" | "aria-activedescendant" | "aria-atomic" | "aria-autocomplete" | "aria-busy" | "aria-checked" | "aria-colcount" | "aria-colindex" | "aria-colspan" | "aria-controls" | "aria-current" | "aria-describedby" | "aria-details" | "aria-disabled" | "aria-dropeffect" | "aria-errormessage" | "aria-expanded" | "aria-flowto" | "aria-grabbed" | "aria-haspopup" | "aria-hidden" | "aria-invalid" | "aria-keyshortcuts" | "aria-label" | "aria-labelledby" | "aria-level" | "aria-live" | "aria-modal" | "aria-multiline" | "aria-multiselectable" | "aria-orientation" | "aria-owns" | "aria-placeholder" | "aria-posinset" | "aria-pressed" | "aria-readonly" | "aria-relevant" | "aria-required" | "aria-roledescription" | "aria-rowcount" | "aria-rowindex" | "aria-rowspan" | "aria-selected" | "aria-setsize" | "aria-sort" | "aria-valuemax" | "aria-valuemin" | "aria-valuenow" | "aria-valuetext" | "children" | "dangerouslySetInnerHTML" | "onCopy" | "onCopyCapture" | "onCut" | "onCutCapture" | "onPaste" | "onPasteCapture" | "onCompositionEnd" | "onCompositionEndCapture" | "onCompositionStart" | "onCompositionStartCapture" | "onCompositionUpdate" | "onCompositionUpdateCapture" | "onFocus" | "onFocusCapture" | "onBlur" | "onBlurCapture" | "onChange" | "onChangeCapture" | "onBeforeInput" | "onBeforeInputCapture" | "onInput" | "onInputCapture" | "onReset" | "onResetCapture" | "onSubmit" | "onSubmitCapture" | "onInvalid" | "onInvalidCapture" | "onLoad" | "onLoadCapture" | "onError" | "onErrorCapture" | "onKeyDown" | "onKeyDownCapture" | "onKeyPress" | "onKeyPressCapture" | "onKeyUp" | "onKeyUpCapture" | "onAbort" | "onAbortCapture" | "onCanPlay" | "onCanPlayCapture" | "onCanPlayThrough" | "onCanPlayThroughCapture" | "onDurationChange" | "onDurationChangeCapture" | "onEmptied" | "onEmptiedCapture" | "onEncrypted" | "onEncryptedCapture" | "onEnded" | "onEndedCapture" | "onLoadedData" | "onLoadedDataCapture" | "onLoadedMetadata" | "onLoadedMetadataCapture" | "onLoadStart" | "onLoadStartCapture" | "onPause" | "onPauseCapture" | "onPlay" | "onPlayCapture" | "onPlaying" | "onPlayingCapture" | "onProgress" | "onProgressCapture" | "onRateChange" | "onRateChangeCapture" | "onSeeked" | "onSeekedCapture" | "onSeeking" | "onSeekingCapture" | "onStalled" | "onStalledCapture" | "onSuspend" | "onSuspendCapture" | "onTimeUpdate" | "onTimeUpdateCapture" | "onVolumeChange" | "onVolumeChangeCapture" | "onWaiting" | "onWaitingCapture" | "onAuxClick" | "onAuxClickCapture" | "onClick" | "onClickCapture" | "onContextMenu" | "onContextMenuCapture" | "onDoubleClick" | "onDoubleClickCapture" | "onDrag" | "onDragCapture" | "onDragEnd" | "onDragEndCapture" | "onDragEnter" | "onDragEnterCapture" | "onDragExit" | "onDragExitCapture" | "onDragLeave" | "onDragLeaveCapture" | "onDragOver" | "onDragOverCapture" | "onDragStart" | "onDragStartCapture" | "onDrop" | "onDropCapture" | "onMouseDown" | "onMouseDownCapture" | "onMouseEnter" | "onMouseLeave" | "onMouseMove" | "onMouseMoveCapture" | "onMouseOut" | "onMouseOutCapture" | "onMouseOver" | "onMouseOverCapture" | "onMouseUp" | "onMouseUpCapture" | "onSelect" | "onSelectCapture" | "onTouchCancel" | "onTouchCancelCapture" | "onTouchEnd" | "onTouchEndCapture" | "onTouchMove" | "onTouchMoveCapture" | "onTouchStart" | "onTouchStartCapture" | "onPointerDown" | "onPointerDownCapture" | "onPointerMove" | "onPointerMoveCapture" | "onPointerUp" | "onPointerUpCapture" | "onPointerCancel" | "onPointerCancelCapture" | "onPointerEnter" | "onPointerEnterCapture" | "onPointerLeave" | "onPointerLeaveCapture" | "onPointerOver" | "onPointerOverCapture" | "onPointerOut" | "onPointerOutCapture" | "onGotPointerCapture" | "onGotPointerCaptureCapture" | "onLostPointerCapture" | "onLostPointerCaptureCapture" | "onScroll" | "onScrollCapture" | "onWheel" | "onWheelCapture" | "onAnimationStart" | "onAnimationStartCapture" | "onAnimationEnd" | "onAnimationEndCapture" | "onAnimationIteration" | "onAnimationIterationCapture" | "onTransitionEnd" | "onTransitionEndCapture" | "ref" | "key">} rest
 * @return {JSX.Element}
 * @constructor
 */
const MobileView: FC<InsightItemProps> = ({
  className,
  icon,
  title,
  farm,
  unseen,
  period,
  group,
  ...rest
}) => (
  <Row
    className={classname("ctra-farmOverview-insightEntry--mobile", styles.InsightItem, className)}
    align="middle"
    gutter={[16, 16]}
    wrap={false}
    {...rest}
  >
    <Col span={23}>
      <Row gutter={[16, 16]}>
        <MobileViewRow hasBadge={unseen} icon={icon} className={styles.Title}>
          <Text>{title}</Text>
        </MobileViewRow>
        {group ? (
          <MobileViewRow>
            <Text>{group}</Text>
          </MobileViewRow>
        ) : null}
        {farm && (
          <MobileViewRow icon={<Icon component={Barn} />}>
            <Tag color="purple">{farm}</Tag>
          </MobileViewRow>
        )}
        <MobileViewRow icon={<CalendarOutlined />}>{period}</MobileViewRow>
      </Row>
    </Col>
    <Col span={1}>
      <Text type="secondary" className={styles.Caret}>
        <RightOutlined />
      </Text>
    </Col>
  </Row>
);

/**
 * Insight entry for the farm overview component
 * @param {React.PropsWithChildren<InsightItemProps>} props
 * @return {JSX.Element}
 * @constructor
 */
const InsightItem: FC<InsightItemProps> = (props) => {
  const { icon, title, farm, period, className, unseen, group, ...rest } = props;
  const { md } = Grid.useBreakpoint();

  return md ? (
    <Row
      align="middle"
      gutter={[16, 0]}
      className={classname("ctra-farmOverview-insightEntry", styles.InsightItem, className)}
      wrap={false}
      {...rest}
    >
      <Col>
        <Badge dot className={classname(unseen ? null : styles.Hidden)} />
      </Col>
      <Col className={styles.Icon}>{icon}</Col>
      <Col className={styles.Title} flex={1}>
        <Text>{title}</Text>
      </Col>
      <Col span={3}>
        <Text>{group}</Text>
      </Col>
      <Col>
        {farm && (
          <Tag color="purple" icon={<Icon component={Barn} />}>
            {farm}
          </Tag>
        )}
      </Col>
      <Col>{period}</Col>
      <Col>
        <Text type="secondary" className={styles.Caret}>
          <RightOutlined />
        </Text>
      </Col>
    </Row>
  ) : (
    <MobileView {...props} />
  );
};

export default InsightItem;
